<template>
  <hr-form-dialog
    ref="dialogRef"
    size="sm"
    :title="title"
    data-e2e="dialog-column-selector"
    @hide="onDialogHide"
    @submit="onSubmit"
  >
    <div class="column q-gutter-y-sm">
      <hr-search-bar
        v-model="search"
        autofocus
        :placeholder="i18n.t('common.tableSearchColumns')"
        data-e2e="input-search-columns"
      />

      <hr-checkbox
        v-if="!isSearching"
        :model-value="isAllSelected(null)"
        :label="i18n.t('common.selectAll')"
        data-e2e="checkbox-select-all"
        @update:model-value="onSelectAll(null, $event)"
      />

      <hr-option-group
        v-model="currentSelected"
        type="checkbox"
        :options="baseOptions"
      />

      <hr-expansion-item
        v-for="optionGroup in optionGroups"
        :key="optionGroup.value"
        :model-value="expandedGroups[optionGroup.value]"
        :label="optionGroup.label"
        data-e2e="expansion-column-group"
        @update:model-value="expandedGroups[optionGroup.value] = $event"
      >
        <hr-expansion-item
          v-for="subOptionGroup in optionGroup.subOptionGroups"
          :key="subOptionGroup.value"
          :model-value="expandedGroups[subGroupKey(optionGroup, subOptionGroup)]"
          :label="subOptionGroup.label"
          data-e2e="expansion-column-sub-group"
          @update:model-value="expandedGroups[subGroupKey(optionGroup, subOptionGroup)] = $event"
        >
          <hr-checkbox
            v-if="!isSearching && subOptionGroup.options.length > 1"
            :model-value="isAllSelected(subOptionGroup.value)"
            :label="i18n.t('common.selectAll')"
            data-e2e="checkbox-select-all-group"
            @update:model-value="onSelectAll(subOptionGroup.value, $event)"
          />
          <hr-option-group
            v-model="currentSelected"
            type="checkbox"
            :options="subOptionGroup.options"
          />
        </hr-expansion-item>
        <hr-checkbox
          v-if="!isSearching && optionGroup.options.length > 1"
          :model-value="isAllSelected(optionGroup.value)"
          :label="i18n.t('common.selectAll')"
          data-e2e="checkbox-select-all-group"
          @update:model-value="onSelectAll(optionGroup.value, $event)"
        />
        <hr-option-group
          v-model="currentSelected"
          type="checkbox"
          :options="optionGroup.options"
        />
      </hr-expansion-item>
    </div>

    <template #actions>
      <hr-supporting-btn
        v-if="isAbleToReorder"
        data-e2e="button-reorder"
        :label="i18n.t('common.buttonReorder')"
        @click="onReorder"
      />
      <hr-dismiss-btn
        data-e2e="button-cancel"
        :label="i18n.t('common.buttonCancel')"
        @click="hide"
      />
      <hr-confirm-btn
        type="submit"
        data-e2e="button-submit"
        :disable="!canSubmit"
        :label="i18n.t('common.buttonSubmit')"
      />
    </template>
  </hr-form-dialog>
</template>

<script setup>
import { i18n } from 'boot/i18n';
import ReorderModal from 'components/UI/ReorderModal';
import {
  HrCheckbox,
  HrConfirmBtn,
  HrDismissBtn,
  HrExpansionItem,
  HrFormDialog,
  HrOptionGroup,
  HrSearchBar,
  HrSupportingBtn,
  useDialog,
} from 'src/design-system';
import { computed, ref, watch } from 'vue';

defineOptions({ name: 'UniversalColumnSelector' });

const props = defineProps({
  options: {
    type: Array,
    required: true,
  },

  /**
   * Group headers, as `{ group, label }` objects, optionally carrying `subGroups` of the same shape.
   */
  optionGroupLabels: {
    type: Object,
    default: null,
  },

  selected: {
    type: Array,
    required: true,
  },

  title: {
    type: String,
    required: true,
  },

  isAbleToReorder: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['hide', 'ok']);

const { openComponentDialog } = useDialog();

const dialogRef = ref(null);
const currentSelected = ref([...props.selected]);
const search = ref('');

const searchTerm = computed(() => search.value.trim().toLowerCase());

const isSearching = computed(() => searchTerm.value !== '');

const canSubmit = computed(() => currentSelected.value.length > 0);

const matchesSearch = (option) => {
  return !isSearching.value || option.label.toLowerCase().includes(searchTerm.value);
};

const groupOptions = (group) => {
  return props.options.filter((option) => option.group === group && matchesSearch(option));
};

const baseOptions = computed(() => groupOptions(null));

/**
 * Builds the rendered group tree from the labels, dropping any group left with nothing to show.
 * Sub-groups are one level deep only.
 */
const buildOptionGroups = (optionGroupLabels, allowSubGroups) => {
  return (optionGroupLabels ?? [])
    .map((optionGroup) => ({
      label: optionGroup.label,
      value: optionGroup.group,
      options: groupOptions(optionGroup.group),
      subOptionGroups: allowSubGroups && optionGroup.subGroups
        ? buildOptionGroups(optionGroup.subGroups, false)
        : [],
    }))
    .filter((optionGroup) => optionGroup.options.length > 0 || optionGroup.subOptionGroups.length > 0);
};

const optionGroups = computed(() => buildOptionGroups(props.optionGroupLabels, true));

const subGroupKey = (optionGroup, subOptionGroup) => `${optionGroup.value}_${subOptionGroup.value}`;

/*
 * Keyed on the same values the template expands by, and built from the labels rather than from the
 * rendered tree: searching hides groups, and an open state that disappeared with them would reset
 * the moment the term was cleared.
 */
const expansionKeys = computed(() => {
  return (props.optionGroupLabels ?? []).flatMap((optionGroup) => [
    {
      key: optionGroup.group,
      groups: [optionGroup.group, ...(optionGroup.subGroups ?? []).map(({ group }) => group)],
    },
    ...(optionGroup.subGroups ?? []).map((subGroup) => ({
      key: `${optionGroup.group}_${subGroup.group}`,
      groups: [subGroup.group],
    })),
  ]);
});

const hasSelection = (groups) => {
  return props.options.some((option) => {
    return groups.includes(option.group) && currentSelected.value.includes(option.value);
  });
};

const collapseToSelection = () => {
  return Object.fromEntries(expansionKeys.value.map(({ key, groups }) => [key, hasSelection(groups)]));
};

const expandedGroups = ref(collapseToSelection());

/*
 * A match inside a collapsed group is invisible, so searching opens everything and clearing returns
 * the groups to the ones holding a selection.
 */
watch(isSearching, (searching) => {
  expandedGroups.value = searching
    ? Object.fromEntries(expansionKeys.value.map(({ key }) => [key, true]))
    : collapseToSelection();
});

const isAllSelected = (group) => {
  const groupOptionValues = props.options
    .filter((option) => option.group === group)
    .map((option) => option.value);
  const selectedCount = groupOptionValues
    .filter((value) => currentSelected.value.includes(value))
    .length;

  if (selectedCount === 0) {
    return false;
  }

  // null rather than false, so a partial selection reads as indeterminate rather than unchecked.
  return selectedCount === groupOptionValues.length ? true : null;
};

const onSelectAll = (group, isSelected) => {
  if (isSelected) {
    currentSelected.value = props.options
      .filter((option) => option.group === group || currentSelected.value.includes(option.value))
      .map((option) => option.value);
    return;
  }

  currentSelected.value = currentSelected.value.filter((value) => {
    return props.options.find((option) => option.value === value)?.group !== group;
  });
};

const show = () => {
  dialogRef.value.show();
};

const hide = () => {
  dialogRef.value?.hide();
};

const onDialogHide = () => {
  emit('hide');
};

const onSubmit = () => {
  emit('ok', currentSelected.value);
  hide();
};

const onReorder = () => {
  openComponentDialog(
    ReorderModal,
    {
      title: i18n.t('common.tableReorderColumns'),
      options: currentSelected.value
        .map((value) => props.options.find((option) => option.value === value))
        .filter(Boolean),
    },
    (newOrder) => {
      currentSelected.value = newOrder;
    },
  );
};

// The dialog plugin calls these on the mounted component.
defineExpose({ show, hide });
</script>
